/**
 * Map controls — floating control stack (top-right of map viewport).
 * Positioned to avoid overlapping with the floating layers panel (top-left)
 * and the overlay-right panel (top-right, 360px wide).
 *
 * Controls: Zoom in/out, compass reset, locate (geolocation), globe/flat toggle.
 */

import { getMap } from './engine';
import { getMapContainer, type MapMode } from './map-container';
import { mapModeToPreference, persistMapModePreference } from './map-mode';
import { mapZoomToGlobeAltitude } from './globe-zoom';
import { activateMeasure } from '@/ui/components/measure-tool';
import { toggleBookmarkDropdown } from '@/ui/components/bookmark-locations';
import { captureMap } from '@/ui/components/map-capture';

/** Globe zoom-button behavior — WM GlobeMap zoomInGlobe/zoomOutGlobe values. */
const GLOBE_ZOOM_IN_FLOOR = 0.05;
const GLOBE_ZOOM_IN_FACTOR = 0.6;
const GLOBE_ZOOM_OUT_CAP = 4.0;
const GLOBE_ZOOM_OUT_FACTOR = 1.6;
const GLOBE_ZOOM_DURATION_MS = 500;

function activeGlobe(): ReturnType<NonNullable<ReturnType<typeof getMapContainer>>['getGlobe']> | null {
  const mc = getMapContainer();
  return mc?.mode === 'globe3d' ? mc.getGlobe() : null;
}

/** Zoom in by one level. */
export function zoomIn(): void {
  const globe = activeGlobe();
  if (globe) {
    const pov = globe.pointOfView();
    // The wheel can reach altitudes outside the button clamp range —
    // never let a clamped "in" move the camera outward.
    if (pov && pov.altitude > GLOBE_ZOOM_IN_FLOOR) {
      globe.pointOfView(
        { ...pov, altitude: Math.max(GLOBE_ZOOM_IN_FLOOR, pov.altitude * GLOBE_ZOOM_IN_FACTOR) },
        GLOBE_ZOOM_DURATION_MS,
      );
    }
    return;
  }
  const map = getMap();
  if (map) map.zoomIn({ duration: 300 });
}

/** Zoom out by one level. */
export function zoomOut(): void {
  const globe = activeGlobe();
  if (globe) {
    const pov = globe.pointOfView();
    if (pov && pov.altitude < GLOBE_ZOOM_OUT_CAP) {
      globe.pointOfView(
        { ...pov, altitude: Math.min(GLOBE_ZOOM_OUT_CAP, pov.altitude * GLOBE_ZOOM_OUT_FACTOR) },
        GLOBE_ZOOM_DURATION_MS,
      );
    }
    return;
  }
  const map = getMap();
  if (map) map.zoomOut({ duration: 300 });
}

/** Reset bearing and pitch to north-up (flat map only). */
export function resetBearing(): void {
  const map = getMap();
  if (map) {
    map.easeTo({ bearing: 0, pitch: 0, duration: 500 });
  }
}

/** Reset to default view. */
export function resetView(): void {
  flyToPreset('global');
}

/** Regional view presets matching Worldmonitor. */
const VIEW_PRESETS: Record<string, { label: string; center: [number, number]; zoom: number }> = {
  global:  { label: 'Global',        center: [0, 20],      zoom: 1.5 },
  america: { label: 'North America', center: [-95, 38],    zoom: 3 },
  eu:      { label: 'Europe',        center: [15, 50],     zoom: 3.5 },
  mena:    { label: 'MENA',          center: [45, 28],     zoom: 3.5 },
  asia:    { label: 'Asia-Pacific',  center: [105, 35],    zoom: 3 },
  africa:  { label: 'Africa',        center: [20, 5],      zoom: 3 },
  latam:   { label: 'Latin America', center: [-60, -15],   zoom: 3 },
  oceania: { label: 'Oceania',       center: [135, -25],   zoom: 3.5 },
};

/** Fly to a named view preset. */
export function flyToPreset(presetId: string): void {
  const preset = VIEW_PRESETS[presetId];
  if (!preset) return;

  const globe = activeGlobe();
  if (globe) {
    globe.pointOfView(
      {
        lat: preset.center[1],
        lng: preset.center[0],
        altitude: mapZoomToGlobeAltitude(preset.zoom),
      },
      1500,
    );
    return;
  }

  const map = getMap();
  if (map) {
    map.flyTo({
      center: preset.center,
      zoom: preset.zoom,
      bearing: 0,
      pitch: 0,
      duration: 1500,
    });
  }
}

// --- Control button factory ---

function createControlBtn(opts: {
  label: string;
  title: string;
  onClick: () => void;
  className?: string;
}): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = `map-ctrl-btn ${opts.className ?? ''}`.trim();
  btn.title = opts.title;
  btn.setAttribute('aria-label', opts.title);
  btn.innerHTML = opts.label;
  btn.onclick = opts.onClick;
  return btn;
}

/** Create and mount the control panel overlay. */
export function mountMapControls(container: HTMLElement): HTMLElement {
  const controls = document.createElement('div');
  controls.className = 'map-controls';

  // 2D/3D mode toggle (WM's map dimension control, relocated to the
  // floating control stack)
  const modeGroup = document.createElement('div');
  modeGroup.className = 'map-ctrl-group map-mode-toggle';

  const requestMode = (mode: MapMode): void => {
    const mc = getMapContainer();
    if (!mc || mc.switching || mc.mode === mode) return;
    void mc.switchMode(mode);
  };

  const btnFlat = createControlBtn({
    label: '2D',
    title: '2D map',
    onClick: () => requestMode('deckgl'),
    className: 'map-mode-btn',
  });
  const btnGlobe = createControlBtn({
    label: '3D',
    title: '3D globe',
    onClick: () => requestMode('globe3d'),
    className: 'map-mode-btn',
  });
  modeGroup.appendChild(btnFlat);
  modeGroup.appendChild(btnGlobe);
  controls.appendChild(modeGroup);

  const syncModeUI = (mode: MapMode): void => {
    const isGlobe = mode === 'globe3d';
    btnFlat.classList.toggle('active', !isGlobe);
    btnGlobe.classList.toggle('active', isGlobe);
    controls.classList.toggle('globe-mode', isGlobe);
  };
  // Initial state before the map finishes init — corrected by the
  // map-mode-changed event dispatched from MapContainer.init().
  syncModeUI(getMapContainer()?.mode ?? 'deckgl');

  const onModeChanged = (e: Event): void => {
    const mode = (e as CustomEvent).detail?.mode as MapMode | undefined;
    if (!mode) return;
    syncModeUI(mode);
    persistMapModePreference(mapModeToPreference(mode));
  };
  window.addEventListener('map-mode-changed', onModeChanged);

  // Zoom group
  const zoomGroup = document.createElement('div');
  zoomGroup.className = 'map-ctrl-group';

  zoomGroup.appendChild(createControlBtn({
    label: '<svg width="16" height="16" viewBox="0 0 16 16"><line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" stroke-width="1.5"/><line x1="8" y1="4" x2="8" y2="12" stroke="currentColor" stroke-width="1.5"/></svg>',
    title: 'Zoom in',
    onClick: zoomIn,
  }));

  zoomGroup.appendChild(createControlBtn({
    label: '<svg width="16" height="16" viewBox="0 0 16 16"><line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" stroke-width="1.5"/></svg>',
    title: 'Zoom out',
    onClick: zoomOut,
  }));

  controls.appendChild(zoomGroup);

  // Navigation group
  const navGroup = document.createElement('div');
  navGroup.className = 'map-ctrl-group';

  // Compass — rotates with map bearing
  const compassBtn = createControlBtn({
    label: '<svg width="16" height="16" viewBox="0 0 16 16"><polygon points="8,2 10,8 8,7 6,8" fill="#ef4444"/><polygon points="8,14 6,8 8,9 10,8" fill="currentColor"/></svg>',
    title: 'Reset north',
    onClick: resetBearing,
    className: 'map-ctrl-compass',
  });
  navGroup.appendChild(compassBtn);

  // Update compass rotation on map move
  const updateCompass = (): void => {
    const map = getMap();
    if (map) {
      const bearing = map.getBearing();
      const svg = compassBtn.querySelector('svg');
      if (svg) svg.style.transform = `rotate(${-bearing}deg)`;
    }
  };

  // Each switch to flat mode creates a NEW MapLibre instance, so the
  // compass listener must re-attach. Poll briefly on mount and after
  // every switch to deckgl mode.
  let compassMap: ReturnType<typeof getMap> = null;
  let compassPoll: ReturnType<typeof setInterval> | null = null;
  const startCompassPoll = (): void => {
    if (compassPoll !== null) clearInterval(compassPoll);
    const pollStart = Date.now();
    compassPoll = setInterval(() => {
      const map = getMap();
      if (map) {
        if (compassPoll !== null) { clearInterval(compassPoll); compassPoll = null; }
        if (map !== compassMap) {
          compassMap = map;
          map.on('move', updateCompass);
          updateCompass();
        }
      } else if (Date.now() - pollStart > 15_000) {
        if (compassPoll !== null) { clearInterval(compassPoll); compassPoll = null; }
        // Only unexpected on the flat map — the globe has no MapLibre instance
        if (getMapContainer()?.mode === 'deckgl') {
          console.warn('[Sentinel] Map did not become available within 15s for compass');
        }
      }
    }, 200);
  };
  startCompassPoll();
  window.addEventListener('map-mode-changed', (e) => {
    if ((e as CustomEvent).detail?.mode === 'deckgl') startCompassPoll();
  });

  // Home / reset to global view
  navGroup.appendChild(createControlBtn({
    label: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M2 8l6-5 6 5"/><path d="M4 7v6h3v-3h2v3h3V7"/></svg>',
    title: 'Reset to global view',
    onClick: resetView,
  }));

  controls.appendChild(navGroup);

  // View presets group
  const presetGroup = document.createElement('div');
  presetGroup.className = 'map-ctrl-group';

  const presetBtn = createControlBtn({
    label: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="8" cy="8" r="5.5"/><line x1="2.5" y1="8" x2="13.5" y2="8"/><ellipse cx="8" cy="8" rx="2.5" ry="5.5"/></svg>',
    title: 'View presets',
    onClick: () => {
      const existing = container.querySelector('.view-preset-dropdown');
      if (existing) { existing.remove(); return; }

      const dropdown = document.createElement('div');
      dropdown.className = 'view-preset-dropdown';
      for (const [id, preset] of Object.entries(VIEW_PRESETS)) {
        const item = document.createElement('button');
        item.className = 'view-preset-item';
        item.textContent = preset.label;
        item.onclick = () => {
          flyToPreset(id);
          dropdown.remove();
        };
        dropdown.appendChild(item);
      }
      presetBtn.parentElement!.appendChild(dropdown);
      // Close on outside click
      const close = (e: MouseEvent): void => {
        if (!dropdown.contains(e.target as Node) && e.target !== presetBtn) {
          dropdown.remove();
          document.removeEventListener('click', close);
        }
      };
      setTimeout(() => document.addEventListener('click', close), 0);
    },
  });
  presetGroup.appendChild(presetBtn);
  controls.appendChild(presetGroup);

  // Tools group — every button is flat-map-only, so the whole group is
  // hidden in globe mode (see .map-tools-group in theme.ts)
  const toolsGroup = document.createElement('div');
  toolsGroup.className = 'map-ctrl-group map-tools-group';

  toolsGroup.appendChild(createControlBtn({
    label: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2"><line x1="3" y1="13" x2="13" y2="3"/><circle cx="3" cy="13" r="2" fill="currentColor" stroke="none"/><circle cx="13" cy="3" r="2" fill="currentColor" stroke="none"/></svg>',
    title: 'Measure distance',
    onClick: activateMeasure,
    className: 'measure-tool-btn',
  }));

  toolsGroup.appendChild(createControlBtn({
    label: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M8 2C5.8 2 4 3.6 4 5.8 4 9.2 8 14 8 14s4-4.8 4-8.2C12 3.6 10.2 2 8 2z"/><circle cx="8" cy="5.8" r="1.5" fill="currentColor" stroke="none"/></svg>',
    title: 'Saved locations',
    onClick: toggleBookmarkDropdown,
    className: 'bookmark-tool-btn',
  }));

  toolsGroup.appendChild(createControlBtn({
    label: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2"><rect x="2" y="3" width="12" height="10" rx="1.5"/><circle cx="6" cy="7.5" r="1.5"/><path d="M2 11l3-3 2.5 2.5L11 7l3 3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    title: 'Capture map view',
    onClick: () => { void captureMap(); },
    className: 'capture-tool-btn',
  }));

  controls.appendChild(toolsGroup);

  container.appendChild(controls);

  // Watch for overlay-right panel visibility to shift controls
  const overlayRight = container.querySelector('#overlay-right');
  if (overlayRight) {
    const shiftCheck = (): void => {
      const hasContent = overlayRight.querySelector('.panel-inner[style*="display: flex"], .panel-inner:not([style*="display: none"])');
      controls.style.right = hasContent ? '384px' : '12px';
    };
    const observer = new MutationObserver(shiftCheck);
    observer.observe(overlayRight, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    shiftCheck();
  }

  return controls;
}
